import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface DateTimeRange {
  from: Date | undefined;
  to: Date | undefined;
  startTime: string;
  endTime: string;
}

interface DateRangePickerProps {
  readonly value: DateTimeRange | undefined;
  readonly onChange: (range: DateTimeRange | undefined) => void;
  readonly className?: string;
}

const DEFAULT_START_TIME = '00:00';
const DEFAULT_END_TIME = '23:59';

export function DateRangePicker({
  value,
  onChange,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const clickCountRef = useRef(0);

  const dateRange: DateRange | undefined =
    value?.from || value?.to ? { from: value?.from, to: value?.to } : undefined;

  const handleDateSelect = (range: DateRange | undefined) => {
    clickCountRef.current += 1;

    onChange({
      from: range?.from,
      to: range?.to,
      startTime: value?.startTime ?? DEFAULT_START_TIME,
      endTime: value?.endTime ?? DEFAULT_END_TIME,
    });
  };

  const handleTimeChange = (field: 'startTime' | 'endTime', time: string) => {
    onChange({
      from: value?.from,
      to: value?.to,
      startTime:
        field === 'startTime' ? time : (value?.startTime ?? DEFAULT_START_TIME),
      endTime:
        field === 'endTime' ? time : (value?.endTime ?? DEFAULT_END_TIME),
    });
  };

  const handleApply = () => {
    setOpen(false);
  };

  const handleReset = () => {
    clickCountRef.current = 0;
    onChange(undefined);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      clickCountRef.current = 0;
      setOpen(true);
    }
    // 닫기는 적용/초기화 버튼으로만 허용
  };

  const formatDisplay = () => {
    if (!value?.from) return '날짜 범위 선택';
    const startTime = value.startTime ?? DEFAULT_START_TIME;
    const endTime = value.endTime ?? DEFAULT_END_TIME;
    const fromStr = `${format(value.from, 'yyyy.MM.dd', { locale: ko })} ${startTime}`;
    if (!value.to) return fromStr;
    const toStr = `${format(value.to, 'yyyy.MM.dd', { locale: ko })} ${endTime}`;
    return `${fromStr} ~ ${toStr}`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-auto justify-start px-3 py-2 text-left text-sm font-normal',
            !value?.from && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDisplay()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[10000] w-auto p-0"
        align="start"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <Calendar
          mode="range"
          selected={dateRange}
          onSelect={handleDateSelect}
          numberOfMonths={2}
          locale={ko}
          disabled={{ after: new Date() }}
        />
        <div className="border-t px-4 py-3 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">
                시작 시간
              </label>
              <input
                type="time"
                value={value?.startTime ?? DEFAULT_START_TIME}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <span className="text-gray-400">~</span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">
                종료 시간
              </label>
              <input
                type="time"
                value={value?.endTime ?? DEFAULT_END_TIME}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-sm"
              onClick={handleReset}
            >
              초기화
            </Button>
            <Button size="sm" className="flex-1 text-sm" onClick={handleApply}>
              적용
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export type { DateTimeRange };
